import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight, Cpu, ShieldCheck, GraduationCap, Layers, Check } from 'lucide-react';

const services = [
  {
    id: 'cross-platform',
    number: '01',
    title: 'Cross-Platform Development',
    icon: Layers,
    summary:
      'Native-feeling apps for iOS, Android, and the web from a single, well-architected codebase.',
    image: 'https://images.unsplash.com/photo-1660165458059-57cfb6cc87e5?w=1600&q=80',
    deliverables: [
      'React Native + Web architecture audit',
      'Design system shared across surfaces',
      'CI/CD with EAS / App Store / Play Store',
      'Native modules where they matter, JS where they don\'t',
      'Performance and accessibility baselines',
    ],
  },
  {
    id: 'ai-integration',
    number: '02',
    title: 'AI Integration',
    icon: Cpu,
    summary:
      'Ship production AI features — chat, retrieval, agents, automation — without the prototype-to-production cliff.',
    image: 'https://images.unsplash.com/photo-1749006590475-4592a5dbf99f?w=1600&q=80',
    deliverables: [
      'LLM model selection & cost modelling',
      'Retrieval-augmented generation (RAG) pipelines',
      'Tool-use and agent orchestration',
      'Evaluation harnesses & regression tests',
      'Observability: prompts, traces, spend, latency',
    ],
  },
  {
    id: 'ai-security',
    number: '03',
    title: 'AI Security',
    icon: ShieldCheck,
    summary:
      'Independent assessment and hardening of your AI surface — before someone else finds the holes.',
    image: 'https://images.pexels.com/photos/5380618/pexels-photo-5380618.jpeg?w=1600',
    deliverables: [
      'Threat modelling of prompts, tools and data flows',
      'Prompt-injection & jailbreak red-teaming',
      'PII / data-exfiltration controls',
      'Guardrails: input, output, and tool-call layers',
      'Incident playbooks and monitoring',
    ],
  },
  {
    id: 'ai-training',
    number: '04',
    title: 'AI Training',
    icon: GraduationCap,
    summary:
      'Practical, hands-on programs that move your team from curiosity to capability.',
    image: 'https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?w=1600',
    deliverables: [
      'Executive briefings on AI risk & opportunity',
      'Engineering bootcamps: LLMs, RAG, agents',
      'Secure-AI workshops for product teams',
      'Custom curricula on your codebase',
      'Ongoing office-hours support',
    ],
  },
];

export default function Services() {
  const location = useLocation();

  React.useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [location]);

  return (
    <div data-testid="services-page">
      {/* HEADER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-16">
        <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
          Services
        </div>
        <h1 className="font-display font-bold tracking-tighter text-5xl md:text-7xl text-ink max-w-4xl leading-[0.95]">
          Four practices, deeply<br />practiced.
        </h1>
        <p className="mt-6 text-lg text-ink-muted max-w-2xl leading-relaxed">
          We deliberately keep our scope narrow so we can go deep. Each engagement
          is led by a senior practitioner — never handed down a chain.
        </p>
      </section>

      {/* SIDE NAV + SERVICES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-3">
            <div className="sticky top-24">
              <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
                Jump to
              </div>
              <ul className="space-y-3">
                {services.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      data-testid={`service-nav-${s.id}`}
                      className="font-display text-lg text-ink-muted hover:text-ink transition-colors"
                    >
                      <span className="font-mono text-xs text-ember mr-3">{s.number}</span>
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-9 space-y-24">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.id}
                  id={s.id}
                  data-testid={`service-detail-${s.id}`}
                  className="scroll-mt-24"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-mono text-xs tracking-widest uppercase text-ember">
                      {s.number}
                    </span>
                    <div className="h-px flex-1 bg-black/10" />
                    <Icon size={22} strokeWidth={1.25} className="text-ink" />
                  </div>

                  <h2 className="font-display font-bold tracking-tighter text-4xl md:text-5xl text-ink mb-4">
                    {s.title}
                  </h2>
                  <p className="text-lg text-ink-muted leading-relaxed max-w-2xl">
                    {s.summary}
                  </p>

                  <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-6 border border-black/10">
                    <div className="md:col-span-2 aspect-[4/3] md:aspect-auto overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="md:col-span-3 p-8 md:p-10">
                      <div className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mb-4">
                        What you get
                      </div>
                      <ul className="space-y-3">
                        {s.deliverables.map((d) => (
                          <li key={d} className="flex items-start gap-3 text-ink">
                            <Check size={18} className="text-ember mt-0.5 shrink-0" strokeWidth={1.75} />
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/contact"
                        data-testid={`service-cta-${s.id}`}
                        className="mt-8 inline-flex items-center gap-2 bg-ink text-cream px-5 py-2.5 text-sm font-medium hover:bg-ember transition-colors"
                      >
                        Discuss this engagement
                        <ArrowUpRight size={14} />
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
